import { z } from 'zod';
import { alertSeveritySchema } from './alert.js';
import { checkStatusSchema } from './check.js';

/**
 * Live health of one external connection, as shown on the settings view.
 *
 * @since 1.0.0
 */
export const connectionStatusSchema = z.object({
    // provider accounts contribute one row each, keyed `provider-api-<account id>`
    key: z.string().min(1),
    label: z.string(),
    status: checkStatusSchema.exclude(['running']),
    statusLabel: z.string(),
    detail: z.string(),
});

export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

export const connectionsResponseSchema = z.object({
    connections: z.array(connectionStatusSchema),
});

export type ConnectionsResponse = z.infer<typeof connectionsResponseSchema>;

/**
 * The notification kinds a route can be configured for: each severity plus recovery.
 *
 * @since 1.3.0
 */
export const alertRouteKeySchema = z.union([alertSeveritySchema, z.literal('recovery')]);

export type AlertRouteKey = z.infer<typeof alertRouteKeySchema>;

/**
 * Where one severity is delivered. A null `channel` means the worker's configured
 * default channel; the info severity is never posted whatever its route says.
 *
 * @since 1.0.0
 */
export const alertRouteSchema = z.object({
    severity: alertRouteKeySchema,
    channel: z.string().nullable(),
    shouldMention: z.boolean(),
});

export type AlertRoute = z.infer<typeof alertRouteSchema>;

export const alertingSettingsSchema = z.object({
    routes: z.array(alertRouteSchema),
    stormThreshold: z.number().int().min(2).max(50),
    stormWindowMinutes: z.number().int().min(1).max(120),
});

export type AlertingSettings = z.infer<typeof alertingSettingsSchema>;

/**
 * Defaults that match the routing table in the approved mockup.
 *
 * @since 1.0.0
 */
export const DEFAULT_ALERTING_SETTINGS: AlertingSettings = {
    routes: [
        { severity: 'critical', channel: null, shouldMention: true },
        { severity: 'warning', channel: null, shouldMention: false },
        { severity: 'info', channel: null, shouldMention: false },
        { severity: 'recovery', channel: null, shouldMention: false },
    ],
    stormThreshold: 5,
    stormWindowMinutes: 10,
};

/**
 * What the dashboard reports about the QC mailbox.
 *
 * @since 1.0.0
 */
export const qcMailboxStatusSchema = z.object({
    address: z.string(),
    isVerified: z.boolean(),
    verifiedAt: z.number().int().nullable(),
    verifiedAddress: z.string().nullable(),
});

export type QcMailboxStatus = z.infer<typeof qcMailboxStatusSchema>;

/**
 * Confirming, or withdrawing confirmation of, the QC mailbox.
 *
 * @since 1.0.0
 */
export const qcMailboxRequestSchema = z.object({
    isVerified: z.boolean(),
});

export type QcMailboxRequest = z.infer<typeof qcMailboxRequestSchema>;
